import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  TuiAppearance,
  TuiButton,
  TuiDialogContext,
  TuiError,
  TuiLabel,
  TuiLink,
  TuiNotification,
  TuiTextfieldComponent,
  TuiTextfieldDirective
} from '@taiga-ui/core';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { TuiForm } from '@taiga-ui/layout';
import { injectContext } from '@taiga-ui/polymorpheus';
import { User } from '../../core/auth/auth.model';
import { PasswordModalStore } from './password-modal.store';
import { PasswordModalFacade } from './password-modal.facade';
import { catchError, EMPTY, tap } from 'rxjs';
import { AppError } from '../../core/error/app-error';
import { ERROR_TYPES } from '../../core/error/error-messages.const';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-password-modal',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TuiAppearance,
    TuiAutoFocus,
    TuiButton,
    TuiButtonLoading,
    TuiError,
    TuiForm,
    TuiLabel,
    TuiTextfieldComponent,
    TuiTextfieldDirective,
    TuiNotification,
    TuiLink
  ],
  templateUrl: './password-modal.html',
  styleUrl: './password-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PasswordModalStore, PasswordModalFacade]
})
export class PasswordModal implements OnInit {
  protected readonly context = injectContext<TuiDialogContext<User | undefined, string>>();
  readonly #passwordModalFacade = inject(PasswordModalFacade);
  protected readonly passwordModalStore = inject(PasswordModalStore);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly password = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(/^\d*$/)]
  });

  protected login() {
    this.clearFormError('userPasswordExpired');
    this.#passwordModalFacade
      .login(this.context.data)
      .pipe(tap(() => this.passwordModalStore.updateIsSentCodeNotificationShown(true)))
      .subscribe();
  }

  protected clearFormError(errorKey: string) {
    const errors = this.password.errors;
    if (!errors?.[errorKey]) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [errorKey]: _removed, ...rest } = errors;
    this.password.setErrors(Object.keys(rest).length ? rest : null);
  }

  protected loginWithPassword() {
    this.passwordModalStore.updateIsSentCodeNotificationShown(false);

    this.#passwordModalFacade
      .login(this.context.data, this.password.value)
      .pipe(
        catchError((error: AppError) => {
          if (error.type === ERROR_TYPES.InvalidPasswordError) {
            this.password.setErrors({
              ...(this.password.errors ?? {}),
              invalidPassword: error.uiMessage
            });
          } else if (error.type === ERROR_TYPES.UserPasswordExpiredError) {
            this.password.setErrors({
              ...(this.password.errors ?? {}),
              userPasswordExpired: true
            });
          }

          return EMPTY;
        })
      )
      .subscribe((user) => this.context.completeWith(user));
  }

  ngOnInit(): void {
    this.password.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.clearFormError('server'));
  }
}
